import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  Alert,
  Button,
  Modal,
  Segmented,
  Spin,
  Typography,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
  listControllersOptions,
  setControllerVersionMutation,
} from "../../../services/licensing/client/@tanstack/react-query.gen";
import type {
  Controller,
  ListControllersResponse,
} from "../../../services/licensing/client/types.gen";
import type { PartnerOutput } from "../../../services/client/types.gen";
import { ControllerCard } from "./ControllerCard";
import {
  ControllerColumns,
  type ControllerDragData,
  type VersionDropData,
} from "./ControllerColumns";
import { controllerStatusOf } from "./controllerStatus";
import {
  compareVersions,
  runningVersionBoardColumns,
  statusBoardColumns,
  targetVersionBoardColumns,
  type ControllersView,
} from "./boardColumns";
import { useOpenSiteAsPartner } from "../useOpenSiteAsPartner";
import {
  listSiteIoConfigurations,
  siteIoConfigurationsBySiteId,
} from "../siteIoConfigurations";
import { siteDetailsTabKeys } from "../../SiteDetails/siteDetailsKeys";

const { Text } = Typography;

const VIEW_DESCRIPTION: Record<ControllersView, string> = {
  target:
    "Columns are the version each controller takes on its next deploy. Drag a controller onto another version to target it there, or add a column for a version nothing runs yet.",
  running: "Columns are the version each controller last reported running.",
  status:
    "Columns compare what each controller reported running against the version it is targeted at.",
};

type ControllersBoardProps = {
  partners?: PartnerOutput[];
};

const extractErrorMessage = (error: unknown): string => {
  const detail = (error as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Please try again.";
};

const controllerName = (controller: Controller) =>
  controller.device_info?.serial_numbers?.hostname ??
  controller._id ??
  "Controller";

export const ControllersBoard: React.FC<ControllersBoardProps> = ({
  partners,
}) => {
  // /admin sits outside the AuthenticationGuard, so the licensing client has
  // no token configured and the admin token must be attached per request.
  const adminAuth = {
    headers: { Authorization: `Bearer ${localStorage.getItem("admin-token")}` },
  };
  const controllersQuery = listControllersOptions(adminAuth);

  const {
    data: controllers,
    isPending,
    isFetching,
    error,
    refetch,
  } = useQuery({ ...controllersQuery, retry: false });

  const { data: siteIoConfigurations } = useQuery({
    queryKey: ["admin-site-io-configurations"],
    retry: false,
    queryFn: listSiteIoConfigurations,
  });
  const ioConfigurationBySiteId = useMemo(
    () => siteIoConfigurationsBySiteId(siteIoConfigurations ?? []),
    [siteIoConfigurations],
  );

  const queryClient = useQueryClient();
  const setVersion = useMutation({
    ...setControllerVersionMutation(adminAuth),
    onSuccess: (updated) => {
      queryClient.setQueryData<ListControllersResponse>(
        controllersQuery.queryKey,
        (previous) =>
          previous?.map((controller) =>
            controller._id === updated._id ? updated : controller,
          ),
      );
      message.success(
        `${controllerName(updated)} now targets ${updated.target_version}`,
      );
    },
    onError: (mutationError) => {
      message.error(
        `Failed to set target version. ${extractErrorMessage(mutationError)}`,
      );
    },
  });

  const openSiteAsPartner = useOpenSiteAsPartner();
  const openControllerSettings = ({ site_id, partner_id }: Controller) => {
    if (site_id && partner_id) {
      openSiteAsPartner.mutate({
        partnerId: partner_id,
        siteId: site_id,
        tab: siteDetailsTabKeys.Controller,
      });
    }
  };

  const partnerNameById = useMemo(
    () =>
      Object.fromEntries(
        (partners ?? []).map((partner) => [partner.partner_id, partner.name]),
      ),
    [partners],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const viewFromUrl = searchParams.get("view");
  const view: ControllersView =
    viewFromUrl === "status" || viewFromUrl === "running"
      ? viewFromUrl
      : "target";
  const setView = (next: ControllersView) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", next);
    setSearchParams(params, { replace: true });
  };

  const [addedVersions, setAddedVersions] = useState<string[]>([]);

  const columns = useMemo(
    () => ({
      status: statusBoardColumns(controllers ?? []),
      running: runningVersionBoardColumns(controllers ?? []),
      target: targetVersionBoardColumns(controllers ?? [], addedVersions),
    }),
    [controllers, addedVersions],
  );

  const addVersionColumn = (version: string) =>
    setAddedVersions((previous) =>
      previous.includes(version) ? previous : [...previous, version],
    );

  const [draggedId, setDraggedId] = useState<string | undefined>();
  const draggedController = controllers?.find(
    (controller) => controller._id === draggedId,
  );

  const sensors = useSensors(
    // Cards carry copyable text, so a plain click must not start a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const setTargetVersion = (controllerId: string, version: string) =>
    setVersion.mutate({
      path: { controller_id: controllerId },
      body: { version },
    });

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setDraggedId(undefined);
    const { controllerId } = (active.data.current ?? {}) as ControllerDragData;
    const { version } = (over?.data.current ?? {}) as VersionDropData;
    const controller = controllers?.find((each) => each._id === controllerId);
    if (!controller || !version || controller.target_version === version) {
      return;
    }

    const current = controller.target_version;
    if (current && compareVersions(version, current) < 0) {
      Modal.confirm({
        title: `Target ${controllerName(controller)} at the older ${version}?`,
        content: `It is targeted at ${current}. Rolling back to ${version} deploys an older version on its next deploy.`,
        okText: "Roll back",
        okButtonProps: { danger: true },
        onOk: () =>
          setVersion.mutateAsync({
            path: { controller_id: controllerId },
            body: { version, allow_backwards: true },
          }),
      });
      return;
    }
    setTargetVersion(controllerId, version);
  };

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load controllers"
        description={extractErrorMessage(error)}
      />
    );
  }

  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spin />
      </div>
    );
  }

  const boardProps = {
    partnerNameById,
    ioConfigurationBySiteId,
    pendingControllerId: setVersion.isPending
      ? setVersion.variables?.path?.controller_id
      : undefined,
    onOpenSettings: openControllerSettings,
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <Text type="secondary" className="max-w-2xl">
          Every registered controller across all sites, including unclaimed
          ones. {VIEW_DESCRIPTION[view]}
        </Text>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented<ControllersView>
            options={[
              { label: "By target version", value: "target" },
              { label: "By running version", value: "running" },
              { label: "By status", value: "status" },
            ]}
            value={view}
            onChange={setView}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </div>
      </div>

      {view === "target" ? (
        <DndContext
          sensors={sensors}
          onDragStart={({ active }: DragStartEvent) =>
            setDraggedId(
              (active.data.current as ControllerDragData | undefined)
                ?.controllerId,
            )
          }
          onDragCancel={() => setDraggedId(undefined)}
          onDragEnd={handleDragEnd}
        >
          <ControllerColumns
            columns={columns.target}
            boardKey="target"
            draggable
            droppable
            onAddVersion={addVersionColumn}
            {...boardProps}
          />

          <DragOverlay>
            {draggedController && (
              <div className="w-64 cursor-grabbing">
                <ControllerCard
                  controller={draggedController}
                  status={controllerStatusOf(draggedController)}
                  partnerName={
                    draggedController.partner_id
                      ? partnerNameById[draggedController.partner_id]
                      : undefined
                  }
                  siteIoConfiguration={
                    draggedController.site_id
                      ? ioConfigurationBySiteId[draggedController.site_id]
                      : undefined
                  }
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        <ControllerColumns
          columns={view === "running" ? columns.running : columns.status}
          boardKey={view}
          {...boardProps}
        />
      )}
    </div>
  );
};
